// Eskom PARCC — the SINGLE definition of "a finding" and its disposition.
//
// Used by the dashboard, the report and the Findings Register so all three count
// the same thing (owner escalation, Jul 2026; closure model reworked R9, Aug 2026).
//
// A finding = a distinct non-compliant CRITERION result. "Additional comments"
// free-text notes and blank-criterion rows are NOT findings; duplicate
// (transaction × criterion) rows collapse to one — that set is `genuineFindings`,
// the findings we IDENTIFIED.
//
// Each identified finding then has ONE disposition:
//   • self-declared — the gap was acknowledged and declared. This is decided at
//     TRANSACTION grain first (Lindiwe's rule, 16 Aug 2026: "when a transaction is
//     self-declared, ALL its findings are cleared with that declaration — direct
//     connection"), then by the finding's own level ("Self-Declared:" variant) or its
//     own "Closed by Self-Declaration" status. Declared, not erased; the tier is kept
//     and it is reported separately.
//   • resolved      — the finding's own status is Closed (fixed by the buyer).
//   • outstanding   — everything else: not yet resolved.
//
// The transaction's self-declared status is passed IN as a boolean — the caller reads
// it from the transaction's own stored compliance level / gap status (a durable
// reviewer decision), NOT from any finding-closure roll-up, so there is no
// findings→transaction→findings cycle (the trap removed on 16 Aug 2026).
//
// Identified = outstanding + self-declared + resolved. The headline "Findings" number
// every scorecard shows is the OUTSTANDING count — declared and resolved findings do
// not register as outstanding (Lindiwe R9, 16 Aug 2026), so a manager reads the number
// still needing work, with the addressed routes shown beside it. Pure; unit-tested.
import type { FindingRead } from '../lib/db';
import { isFindingLevel } from './dashboard';
import { isSelfDeclaredLevel } from './gaps';
import { normalizeFindingStatus } from './normalize';

/** The compliance level a finding is judged on: its final result, else the suggestion. */
function findingLevel(f: FindingLike): string {
  return String(f.finalCompliance ?? f.suggestedCompliance ?? '').trim();
}

/** The minimal finding shape the disposition/dedup logic reads (FindingRead fits). */
export interface FindingLike {
  id: string;
  referenceNo?: string;
  criterion?: string;
  deleted?: boolean;
  legacyStatus?: string;
  effectiveStatus?: string;
  finalCompliance?: string;
  suggestedCompliance?: string;
}

/**
 * True when a finding row is a genuine non-compliance against a criterion — not a
 * soft-deleted row, not an "Additional comments" note, and its own compliance level
 * carries a risk weight (Possible IE / Policy & Procedures / Housekeeping and their
 * self-declared variants). Compliant / Not-Applicable / Pending / blank lines are
 * not findings.
 */
function isGenuine(f: FindingLike): boolean {
  return !f.deleted && f.legacyStatus !== 'Additional comments' && isFindingLevel(findingLevel(f));
}

/**
 * The canonical criterion match token: case- and whitespace-insensitive. Empty
 * (a finding with no criterion) is `''`. The ONE normalisation the dedup key and
 * the reconcile matcher ({@link ../lib/findingReconcile}) both key off, so "one
 * finding per (transaction × criterion)" means the same thing on both paths.
 */
export function normalizeCriterion(criterion: string | undefined | null): string {
  return String(criterion ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/**
 * Dedup key: one finding per (transaction × criterion), matched case- and
 * whitespace-insensitively. A row with no criterion stands alone (keyed by its id)
 * so distinct un-criterioned findings are never merged into one.
 */
function dedupKey(f: FindingLike): string {
  const crit = normalizeCriterion(f.criterion);
  return crit ? `${f.referenceNo ?? ''} ${crit}` : ` id ${f.id}`;
}

/**
 * The findings we IDENTIFIED — the one definition every surface selects through.
 * Non-deleted, risk-weighted non-compliant, not an "Additional comments" note, and
 * deduplicated by (transaction × criterion). Input order is preserved; the first row
 * of each dedup group is the representative.
 */
export function genuineFindings(findings: readonly FindingRead[]): FindingRead[] {
  const seen = new Set<string>();
  const out: FindingRead[] = [];
  for (const f of findings) {
    if (!isGenuine(f)) continue;
    const k = dedupKey(f);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(f);
  }
  return out;
}

/** A finding's disposition. */
export type FindingDisposition = 'outstanding' | 'self-declared' | 'resolved';

/**
 * Classify a finding. A self-declared TRANSACTION clears all its findings (pass
 * `transactionSelfDeclared` — the caller reads it from the transaction's stored
 * compliance level / gap status, {@link buildSelfDeclaredRefs}); then a self-declared
 * own level or status; then an explicit Closed status is resolved; everything else —
 * Open / In Progress / an unrecorded status on a live gap — is outstanding.
 */
export function findingDisposition(f: FindingLike, transactionSelfDeclared = false): FindingDisposition {
  if (transactionSelfDeclared) return 'self-declared';
  if (isSelfDeclaredLevel(findingLevel(f))) return 'self-declared';
  const status = normalizeFindingStatus(f.effectiveStatus ?? f.legacyStatus ?? '');
  if (status === 'Closed by Self-Declaration') return 'self-declared';
  if (status === 'Closed') return 'resolved';
  return 'outstanding';
}

/**
 * The findings still OUTSTANDING — identified, minus those on a self-declared
 * transaction (`selfDeclaredRefs`) and those self-declared or resolved on their own.
 */
export function outstandingFindings(
  findings: readonly FindingRead[],
  selfDeclaredRefs?: ReadonlySet<string>,
): FindingRead[] {
  return genuineFindings(findings).filter(
    (f) => findingDisposition(f, selfDeclaredRefs?.has(f.referenceNo ?? '') ?? false) === 'outstanding',
  );
}

/** A finding is CLOSED (addressed) when its disposition is not outstanding. */
export function findingClosed(f: FindingLike, transactionSelfDeclared = false): boolean {
  return findingDisposition(f, transactionSelfDeclared) !== 'outstanding';
}

/** A finding is OPEN (outstanding) when it is not closed. */
export function findingOpen(f: FindingLike, transactionSelfDeclared = false): boolean {
  return findingDisposition(f, transactionSelfDeclared) === 'outstanding';
}

export interface FindingSummary {
  /** Distinct genuine findings in view (outstanding + selfDeclared + resolved). */
  identified: number;
  /** Findings still to be resolved — the headline "Findings" scorecard number. */
  outstanding: number;
  /** Findings the buyer acknowledged and declared (kept, reported separately). */
  selfDeclared: number;
  /** Findings the buyer fixed (own status Closed). */
  resolved: number;
}

/**
 * Count genuine findings and split them by disposition. By construction,
 * `outstanding + selfDeclared + resolved === identified`. `selfDeclaredRefs` carries
 * the transactions the reviewer self-declared (from their stored compliance level, not
 * a finding roll-up — so no cycle); every finding on one of those transactions is
 * self-declared.
 */
export function summariseFindings(
  findings: readonly FindingRead[],
  selfDeclaredRefs?: ReadonlySet<string>,
): FindingSummary {
  const genuine = genuineFindings(findings);
  let outstanding = 0;
  let selfDeclared = 0;
  let resolved = 0;
  for (const f of genuine) {
    const d = findingDisposition(f, selfDeclaredRefs?.has(f.referenceNo ?? '') ?? false);
    if (d === 'outstanding') outstanding += 1;
    else if (d === 'self-declared') selfDeclared += 1;
    else resolved += 1;
  }
  return { identified: genuine.length, outstanding, selfDeclared, resolved };
}
